import { useState } from 'react';
import { api } from '../../api/client.js';
import {
  Alert,
  Field,
  Notice,
  PageHeader,
  StatusPill,
  datum,
  fehlertext,
  pct,
  useLoad,
} from '../shared/ui.js';

type Dimension = 'PRODUCT_TYPE' | 'MATERIAL';

interface Regel {
  id: string;
  name: string;
  reason: string;
  dimension: Dimension;
  discountPercent: string;
  validFrom: string;
  validTo: string | null;
  isActive: boolean;
  createdBy: { displayName: string };
  activatedBy: { displayName: string } | null;
  activatedAt: string | null;
  deactivatedBy: { displayName: string } | null;
  deactivatedAt: string | null;
  product: {
    shortName: string;
    typeValue: { code: string };
    standardValue: { label: { de?: string } } | null;
  } | null;
  materialValue: { code: string; label: { de?: string } } | null;
  _count: { exemptions: number };
}

interface Reichweite {
  matchingActiveArticles: number;
  activeAccounts: number;
  exemptionCount: number;
  potentiallyAffectedAccounts: number;
}

function bezugText(r: Pick<Regel, 'dimension' | 'product' | 'materialValue'>): string {
  if (r.dimension === 'PRODUCT_TYPE') {
    return r.product
      ? `Produkttyp ${r.product.typeValue.code} – ${r.product.shortName}${
          r.product.standardValue?.label?.de ? ` ${r.product.standardValue.label.de}` : ''
        }`
      : 'Produkttyp';
  }
  return r.materialValue
    ? `Werkstoff ${r.materialValue.code} – ${r.materialValue.label?.de ?? ''}`
    : 'Werkstoff';
}

/**
 * Zentrale Pflege globaler Rabattregeln, ADR-0011. Nur `operator_admin` erreicht diese
 * Seite (Navigation und Routing in `AdminShell`); der Server erzwingt dieselbe Grenze
 * zusaetzlich (`GlobalOverridesController`).
 */
export function GlobalOverridesPage(): React.JSX.Element {
  const { data, error, reload } = useLoad<{ items: Regel[] }>('/api/admin/overrides');
  const [neuOffen, setNeuOffen] = useState(false);
  const regeln = data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Globale Rabattregeln"
        lead="Zentrale, zeitlich begrenzte Rabattobergrenze fuer einen Produkttyp oder Werkstoff. Eine Regel senkt den kundenspezifischen Rabatt, erhoeht ihn aber nie -- sie ersetzt keine Accountkondition."
        actions={
          !neuOffen && (
            <button type="button" className="primary" onClick={() => setNeuOffen(true)}>
              Neue Regel
            </button>
          )
        }
      />
      <Alert text={error} />

      {neuOffen && (
        <NeueRegel
          onDone={() => {
            setNeuOffen(false);
            reload();
          }}
          onCancel={() => setNeuOffen(false)}
        />
      )}

      <section className="card">
        <h3>Regeln</h3>
        {regeln.length === 0 ? (
          <p className="muted">Noch keine globale Regel angelegt.</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Bezug</th>
                  <th className="num">Obergrenze</th>
                  <th>Gueltig</th>
                  <th>Status</th>
                  <th className="num">Ausnahmen</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {regeln.map((r) => (
                  <RegelZeile key={r.id} regel={r} onChange={reload} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function RegelZeile({
  regel,
  onChange,
}: {
  regel: Regel;
  onChange: () => void;
}): React.JSX.Element {
  const [offen, setOffen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reichweite = useLoad<Reichweite>(
    offen && !regel.isActive ? `/api/admin/overrides/${regel.id}/reach` : null,
  );

  async function aktivieren(): Promise<void> {
    if (
      !window.confirm(
        `Regel "${regel.name}" aktivieren? Sie greift ab sofort in der Rabattfindung ` +
          'und ist danach nur noch deaktivierbar, nicht mehr inhaltlich aenderbar.',
      )
    )
      return;
    setErr(null);
    try {
      await api.post(`/api/admin/overrides/${regel.id}/activate`);
      onChange();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  async function deaktivieren(): Promise<void> {
    if (
      !window.confirm(
        `Regel "${regel.name}" deaktivieren? Danach greifen die Accountkonditionen ` +
          'der betroffenen Konten ohne weitere Handlung wieder.',
      )
    )
      return;
    setErr(null);
    try {
      await api.post(`/api/admin/overrides/${regel.id}/deactivate`);
      onChange();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <>
      <tr>
        <td>
          <button type="button" className="link" onClick={() => setOffen(!offen)}>
            {offen ? '▾' : '▸'} {regel.name}
          </button>
        </td>
        <td>{bezugText(regel)}</td>
        <td className="num">
          <strong>{pct(regel.discountPercent)}</strong>
        </td>
        <td>
          ab {datum(regel.validFrom)}
          {regel.validTo ? `, bis ${datum(regel.validTo)}` : ', unbefristet'}
        </td>
        <td>
          <StatusPill active={regel.isActive} activeText="aktiv" inactiveText="Entwurf" />
        </td>
        <td className="num">{regel._count.exemptions}</td>
        <td>
          {regel.isActive ? (
            <button type="button" className="link" onClick={() => void deaktivieren()}>
              deaktivieren
            </button>
          ) : (
            <button type="button" className="link" onClick={() => void aktivieren()}>
              aktivieren
            </button>
          )}
        </td>
      </tr>
      {offen && (
        <tr>
          <td colSpan={7}>
            <Alert text={err} />
            <dl className="kopfdaten wide">
              <dt>Begruendung</dt>
              <dd>{regel.reason}</dd>
              <dt>Angelegt</dt>
              <dd>{regel.createdBy.displayName}</dd>
              {regel.isActive && regel.activatedBy && (
                <>
                  <dt>Aktiviert</dt>
                  <dd>
                    {regel.activatedBy.displayName}
                    {regel.activatedAt ? `, ${datum(regel.activatedAt)}` : ''}
                  </dd>
                </>
              )}
              {!regel.isActive && regel.deactivatedBy && (
                <>
                  <dt>Zuletzt deaktiviert</dt>
                  <dd>
                    {regel.deactivatedBy.displayName}
                    {regel.deactivatedAt ? `, ${datum(regel.deactivatedAt)}` : ''}
                  </dd>
                </>
              )}
            </dl>
            {!regel.isActive && (
              <>
                <h4>Potenzielle Reichweite</h4>
                <p className="note">
                  Zaehlwerte aus Artikel- und Kontostamm -- ausdruecklich{' '}
                  <strong>keine</strong> Preis- oder Umsatzsimulation.
                </p>
                {reichweite.loading && <p>Laedt…</p>}
                {reichweite.data && (
                  <dl className="kopfdaten wide">
                    <dt>Passende aktive Katalogartikel</dt>
                    <dd>{reichweite.data.matchingActiveArticles}</dd>
                    <dt>Aktive Konten insgesamt</dt>
                    <dd>{reichweite.data.activeAccounts}</dd>
                    <dt>Bereits gepflegte Kontoausnahmen</dt>
                    <dd>{reichweite.data.exemptionCount}</dd>
                    <dt>Potenziell betroffene Konten</dt>
                    <dd>
                      <strong>{reichweite.data.potentiallyAffectedAccounts}</strong>
                    </dd>
                  </dl>
                )}
              </>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function NeueRegel({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [dimension, setDimension] = useState<Dimension>('PRODUCT_TYPE');
  const [produktRef, setProduktRef] = useState('');
  const [werkstoffRef, setWerkstoffRef] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const produkte = useLoad<{
    items: Array<{
      id: string;
      shortName: string;
      typeValue: { code: string };
      standardValue: { label: { de?: string } } | null;
    }>;
  }>(dimension === 'PRODUCT_TYPE' ? '/api/admin/master/products' : null);
  const werkstoffe = useLoad<{
    items: Array<{ id: string; code: string; label: { de?: string }; isActive: boolean }>;
  }>(dimension === 'MATERIAL' ? '/api/admin/master/material' : null);

  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    try {
      await api.post('/api/admin/overrides', {
        name,
        reason,
        dimension,
        discountPercent: Number(discountPercent),
        validFrom,
        validTo: validTo || undefined,
        ...(dimension === 'PRODUCT_TYPE' ? { productId: produktRef } : {}),
        ...(dimension === 'MATERIAL' ? { materialValueId: werkstoffRef } : {}),
      });
      onDone();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <section className="card highlight">
      <h3>Neue globale Regel</h3>
      <Notice>
        Die Regel wird als <strong>Entwurf</strong> angelegt. Erst die Aktivierung wirkt
        in der Rabattfindung; davor kann die Reichweite eingesehen werden.
      </Notice>
      <form onSubmit={speichern}>
        <div className="grid2">
          <Field label="Bezug">
            <select
              value={dimension}
              onChange={(e) => {
                setDimension(e.target.value as Dimension);
                setProduktRef('');
                setWerkstoffRef('');
              }}
            >
              <option value="PRODUCT_TYPE">Produkttyp</option>
              <option value="MATERIAL">Werkstoff</option>
            </select>
          </Field>
          {dimension === 'PRODUCT_TYPE' ? (
            <Field label="Produkt">
              <select
                required
                value={produktRef}
                onChange={(e) => setProduktRef(e.target.value)}
              >
                <option value="">– waehlen –</option>
                {(produkte.data?.items ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.typeValue.code} – {p.shortName}
                    {p.standardValue?.label?.de ? ` ${p.standardValue.label.de}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field label="Werkstoff">
              <select
                required
                value={werkstoffRef}
                onChange={(e) => setWerkstoffRef(e.target.value)}
              >
                <option value="">– waehlen –</option>
                {(werkstoffe.data?.items ?? [])
                  .filter((m) => m.isActive)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.code} – {m.label?.de ?? ''}
                    </option>
                  ))}
              </select>
            </Field>
          )}
          <Field label="Name">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
            />
          </Field>
          <Field label="Rabattobergrenze in %">
            <input
              type="number"
              required
              min={0}
              max={40}
              step="0.5"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
            />
          </Field>
          <Field label="Gueltig ab">
            <input
              type="date"
              required
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
            />
          </Field>
          <Field label="Gueltig bis" hint="Leer lassen fuer unbefristet.">
            <input
              type="date"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Begruendung">
          <input
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
          />
        </Field>
        <Alert text={err} />
        <div className="row">
          <button type="submit" className="primary">
            Als Entwurf anlegen
          </button>
          <button type="button" onClick={onCancel}>
            Abbrechen
          </button>
        </div>
      </form>
    </section>
  );
}
